import { useDispatch, useSelector } from 'react-redux';
import * as O from 'fp-ts/Option';
import * as Hooks from 'preact/hooks';
import { ModaSofa } from '@dora/backpack';
import * as NavigationActions from '../../../store/actions/navigation';
import * as FurnitureList from '../../../store/selectors/furnitureList';
import { useSaveLinkState } from '../../hooks/useSaveLinkState';
import { LayoutPostData } from '../../../data/shareLayout';

/** The state representing the current thing being displayed in the drawer */
export type SceneDrawerContentState =
    | 'scene-list' // this now represents having no drawer open
    | 'catalogue'
    | 'configure'
    | 'floor-size'
    | 'share'
    | 'exit-screen'
    | 'basket'
    | 'ar-runway'
    | 'ar-mobile-qr'
    | 'global-colour-config';

export function isScenePanelContentState(view: SceneDrawerContentState) {
    // Is there a better way to narrow this type?
    return (
        view === 'basket' || view === 'exit-screen' || view === 'share' || view === 'ar-mobile-qr'
    );
}

// Title of each drawer
// TODO: delete?
export const titles: Record<SceneDrawerContentState, string> = {
    'scene-list': 'My outdoor space',
    catalogue: 'Furnish your space',
    configure: 'Configure your sofa',
    basket: 'Finalise and purchase',
    'exit-screen': 'Return to your space at any time',
    share: 'Save and share',
    'ar-runway': 'View in AR',
    'ar-mobile-qr': 'View in AR',
    'floor-size': 'Edit floor size',
    'global-colour-config': '',
};

export type ViewActions = {
    toSceneList: () => void;
    toCatalogue: () => void;
    toConfigure: () => void;
    toShare: () => void;
    toBasket: () => void;
    toGenerateLink: () => void;
    toArMobileQr: () => void;
    toFloorSize: () => void;
    toGlobalColourConfig: () => void;
};

/**
 * Hook to see the current state of the scene's view, along with functions to control it.
 *
 * See the `components` directory README for details on the Finite State Machine being constructed
 * here.
 */
export const useSceneDrawerState = (generateNewSharingUrl: (ar: boolean) => void) => {
    const [currentView, setCurrent] = Hooks.useState<SceneDrawerContentState>('scene-list');
    const [prevView, setPrevView] = Hooks.useState<SceneDrawerContentState | null>(null);
    const [open, setOpen] = Hooks.useState<boolean>(true);
    const [currentSofaConfiguration, setCurrentConfiguration] = Hooks.useState<
        O.Option<{ sofaId: string; configuration: ModaSofa.Configuration }>
    >(O.none);

    const dispatch = useDispatch();
    const furnitureCount = useSelector(FurnitureList.furnitureListLength);

    /** Return to the original view configuration */
    const reset = () => {
        setCurrent('scene-list');
        setPrevView(null);
        setOpen(true);
        setCurrentConfiguration(O.none);
    };

    // could these view update functions go a step further and provide the correct ActionBars and
    // Drawer Output components?
    const toSceneList = () => {
        setPrevView(null);
        setCurrent(last => {
            if (last !== 'scene-list') {
                return 'scene-list';
            }
            return last;
        });
    };

    const toCatalogue = () => {
        setPrevView('scene-list');
        dispatch(NavigationActions.viewCatalogueAction());
        setCurrent(last => {
            if (last !== 'catalogue') {
                return 'catalogue';
            }
            return last;
        });
    };

    /** Include a Configuration to reconfigure a sofa */
    const toConfigure = (sofaId?: string, configuration?: ModaSofa.Configuration) => {
        setPrevView(currentView);
        setCurrent(last => {
            if (last !== 'configure') {
                setCurrentConfiguration(
                    sofaId && configuration ? O.some({ configuration, sofaId }) : O.none,
                );
                return 'configure';
            }
            return last;
        });
    };

    const toShare = () => {
        generateNewSharingUrl(false);
        const location = 'items list';
        dispatch(NavigationActions.shareLinkCreatedAction({ furnitureCount, location }));

        setPrevView(currentView);
        setCurrent(last => {
            if (last !== 'share') {
                return 'share';
            }
            return last;
        });
    };

    const toExitScreen = () => {
        generateNewSharingUrl(false);
        const location = 'close dialog';
        dispatch(NavigationActions.shareLinkCreatedAction({ furnitureCount, location }));
        dispatch(NavigationActions.exitViewShownAction());
        setPrevView(currentView);
        setCurrent('exit-screen');
    };

    const toBasket = () => {
        if (prevView !== 'share') {
            setPrevView(currentView);
        }
        setCurrent(last => {
            if (last !== 'basket') {
                return 'basket';
            }
            return last;
        });
    };
    const toArMobileQr = () => {
        generateNewSharingUrl(true);
        dispatch(
            NavigationActions.shareLinkCreatedAction({ furnitureCount, location: 'ar-desktop' }),
        );
        setPrevView('scene-list');
        setCurrent('ar-mobile-qr');
    };

    const toFloorSize = () => {
        setPrevView(currentView);
        setCurrent('floor-size');
    };

    const toGlobalColourConfig = () => {
        setPrevView(currentView);
        setCurrent('global-colour-config');
    };

    // TODO: delete?
    const toPrevView = () => {
        switch (prevView) {
            case 'catalogue':
                return toCatalogue();
            case 'scene-list':
                return toSceneList();
            case 'configure':
                return toConfigure();
            case 'share':
                return toSceneList();
            case 'basket':
                return toBasket();
            case 'exit-screen':
                return toSceneList();
            case 'floor-size':
                return toFloorSize();
            case 'global-colour-config':
                return toGlobalColourConfig();
        }
    };

    const toggleOpen = () => setOpen(last => !last);
    const fullHeightView = isScenePanelContentState(currentView);
    const title = titles[currentView];

    return {
        open,
        toggleOpen,
        currentView,
        currentSofaConfiguration,
        prevView,
        fullHeightView,
        title,
        viewActions: {
            toSceneList,
            toCatalogue,
            toConfigure,
            toShare,
            toPrevView,
            reset,
            toExitScreen,
            toBasket,
            toArMobileQr,
            toFloorSize,
            toGlobalColourConfig,
        },
    };
};
